import { useMemo, useState } from "react";
import { AlertTriangle, Download, Sparkles } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { useLookup } from "../../context/LookupContext.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { Checkbox } from "../../components/ui/checkbox.js";
import { FileInput } from "../../components/ui/file-input.js";
import { Input } from "../../components/ui/input.js";
import { SelectField } from "../../components/ui/select-field.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table.js";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog.js";
import { LockedNotice } from "./shared.js";

type Status = "new" | "adds" | "exists";

interface PreviewItem {
  key: string;
  skillTypeId: string;
  typeName: string;
  name: string;
  cleanName: string;
  rawNames: string[];
  existingSkillId: string | null;
  existingName: string | null;
  matched: boolean;
  status: Status;
  departmentIds: string[];
  roles: string[];
  alsoApplies: { departmentId: string; role: string }[];
}

interface AnalyzeResult {
  items: PreviewItem[];
  skippedRows: { rowNumber: number; reason: string }[];
  ignoredColumns: string[];
  departmentNames: Record<string, string>;
  aiUsed: boolean;
}

// HR's choices on one preview row.
interface RowChoice {
  include: boolean;
  useExisting: boolean;
  name: string;
}

const STATUS_BADGE: Record<Status, { tone: "success" | "info" | "neutral"; label: string }> = {
  new: { tone: "success", label: "New" },
  adds: { tone: "info", label: "Adds departments / roles" },
  exists: { tone: "neutral", label: "Already there" },
};

// The formatted Excel template (bold sky-blue header, borders, fitted columns),
// fetched with the session so the 401-refresh interceptor still applies.
async function downloadTemplate() {
  const res = await api.get("/skills/import/template", { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "skills-import-template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Skills import: upload a Department | Roles | Technical | Soft | Functional
// sheet, review what the AI cleaned and matched, then import. Nothing is saved
// until "Import". Imported skills are default skills for those departments and
// roles, so matching employees get them straight away.
export default function SkillImportDialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const toast = useToast();
  const roleLabels = useLookup("ROLE_LABEL");
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<AnalyzeResult | null>(null);
  const [choices, setChoices] = useState<Record<string, RowChoice>>({});

  const roleName = (r: string) => roleLabels.find((l) => l.value === r)?.displayName ?? r.replace(/_/g, " ");
  const deptName = (id: string) => result?.departmentNames[id] ?? id;

  function reset() {
    setFile(null);
    setResult(null);
    setChoices({});
  }

  function close() {
    reset();
    onClose();
  }

  async function analyze() {
    if (!file) throw new Error("validation");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post<AnalyzeResult>("/skills/import/analyze", form);
      setResult(data);
      setChoices(
        Object.fromEntries(
          data.items.map((it) => [it.key, { include: it.status !== "exists", useExisting: it.matched, name: it.name }]),
        ),
      );
    } catch (e) {
      logError("SkillImportDialog.analyze", e);
      toast.show(errMsg(e) ?? "Could not read the file", "error");
      throw e;
    }
  }

  // What will actually be sent: included rows, minus matches that are already
  // fully in place (unless HR switched them to a new skill).
  const toImport = useMemo(
    () =>
      (result?.items ?? []).filter((it) => {
        const c = choices[it.key];
        if (!c?.include) return false;
        return !(it.status === "exists" && c.useExisting);
      }),
    [result, choices],
  );

  async function commit() {
    try {
      const items = toImport.map((it) => {
        const c = choices[it.key]!;
        const asNew = !c.useExisting || !it.existingSkillId;
        return {
          skillTypeId: it.skillTypeId,
          name: asNew ? c.name.trim() : it.name,
          existingSkillId: asNew ? null : it.existingSkillId,
          departmentIds: it.departmentIds,
          roles: it.roles,
        };
      });
      if (items.some((i) => !i.name)) {
        toast.show("Every new skill needs a name", "error");
        throw new Error("validation");
      }
      const { data } = await api.post<{ created: number; updated: number; unchanged: number; employeesAssigned: number }>(
        "/skills/import/commit",
        { items },
      );
      toast.show(
        `Imported: ${data.created} new, ${data.updated} updated, ${data.unchanged} unchanged · ${data.employeesAssigned} employee skill${data.employeesAssigned === 1 ? "" : "s"} assigned`,
        "success",
      );
      onImported();
      close();
    } catch (e) {
      if (!(e instanceof Error && e.message === "validation")) {
        logError("SkillImportDialog.commit", e);
        toast.show(errMsg(e) ?? "Could not import the skills", "error");
      }
      throw e;
    }
  }

  const setChoice = (key: string, patch: Partial<RowChoice>) =>
    setChoices((c) => ({ ...c, [key]: { ...c[key]!, ...patch } }));

  const counts = useMemo(() => {
    const items = result?.items ?? [];
    return {
      new: items.filter((i) => i.status === "new").length,
      adds: items.filter((i) => i.status === "adds").length,
      exists: items.filter((i) => i.status === "exists").length,
    };
  }, [result]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Import skills</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="flex flex-col gap-4 text-[13px]">
            <div className="rounded-md bg-surface-50 px-4 py-3">
              <p className="font-medium">
                Columns: Department · Roles · Technical Skills · Soft Skills · Functional Skills
              </p>
              <ul className="mt-1 list-disc pl-5 text-surface-600">
                <li>Several skills in one cell are separated by commas, e.g. "AutoCAD, Aramis".</li>
                <li>Leave Department blank (or merged) for the rows under the same department.</li>
                <li>The column decides the skill type. Other columns (Designation, Office…) are ignored.</li>
                <li>
                  AI fixes spelling and finds skills that already exist. You review everything before it is saved.
                </li>
              </ul>
              <Button variant="outline" size="sm" className="mt-3" title="Download a sample sheet in the import format" onClick={() =>
                  void downloadTemplate().catch((e) => {
                    logError("SkillImportDialog.template", e);
                    toast.show(errMsg(e) ?? "Could not download the template", "error");
                  })
                }
              >
                <Download size={14} /> Download template
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <FileInput accept=".xlsx,.csv" value={file} onChange={setFile} placeholder="Choose a .xlsx or .csv file" />
              <AsyncButton disabled={!file} onClick={() => analyze()}>
                <Sparkles size={14} /> Analyse
              </AsyncButton>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 text-[13px]">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="success">{counts.new} new</Badge>
              <Badge tone="info">{counts.adds} add departments / roles</Badge>
              <Badge tone="neutral">{counts.exists} already there</Badge>
              {result.skippedRows.length > 0 && <Badge tone="warning">{result.skippedRows.length} rows skipped</Badge>}
              {!result.aiUsed && (
                <Badge tone="warning">AI clean-up unavailable — exact matching only</Badge>
              )}
            </div>

            {result.items.length === 0 ? (
              <LockedNotice>No skills to import were found in this file.</LockedNotice>
            ) : (
              <div className="max-h-[50vh] overflow-auto rounded-md border border-surface-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 pl-3" />
                      <TableHead>Type</TableHead>
                      <TableHead>Skill</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Departments</TableHead>
                      <TableHead>Roles</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.items.map((it) => {
                      const c = choices[it.key]!;
                      const asNew = !c.useExisting || !it.existingSkillId;
                      const fixed = it.rawNames.filter((r) => r !== (asNew ? c.name : it.name));
                      const done = it.status === "exists" && c.useExisting;
                      return (
                        <TableRow key={it.key}>
                          <TableCell className="pl-3 align-top">
                            <Checkbox
                              checked={c.include && !done}
                              disabled={done}
                              aria-label={`Import ${it.name}`}
                              onCheckedChange={(v) => setChoice(it.key, { include: v === true })}
                            />
                          </TableCell>
                          <TableCell className="align-top text-surface-500">{it.typeName}</TableCell>
                          <TableCell className="min-w-56 align-top">
                            {asNew ? (
                              <Input
                                value={c.name}
                                aria-label="Skill name"
                                className="h-8"
                                onChange={(e) => setChoice(it.key, { name: e.target.value })}
                              />
                            ) : (
                              <span className="font-medium">{it.name}</span>
                            )}
                            {fixed.length > 0 && (
                              <p className="mt-1 text-[12px] text-surface-400">From file: {fixed.join(", ")}</p>
                            )}
                            {it.matched && it.existingSkillId && (
                              <SelectField
                                size="sm"
                                className="mt-1 w-56"
                                aria-label="Use the existing skill or add a new one"
                                value={c.useExisting ? "existing" : "new"}
                                onValueChange={(v) =>
                                  setChoice(it.key, {
                                    useExisting: v === "existing",
                                    include: true,
                                    name: v === "existing" ? it.name : it.cleanName,
                                  })
                                }
                              >
                                <option value="existing">Use existing “{it.existingName}”</option>
                                <option value="new">Add as a new skill</option>
                              </SelectField>
                            )}
                          </TableCell>
                          <TableCell className="align-top">
                            <Badge tone={asNew ? "success" : STATUS_BADGE[it.status].tone}>
                              {asNew ? "New" : STATUS_BADGE[it.status].label}
                            </Badge>
                          </TableCell>
                          <TableCell className="align-top">{it.departmentIds.map(deptName).join(", ")}</TableCell>
                          <TableCell className="align-top">
                            {it.roles.map(roleName).join(", ")}
                            {it.alsoApplies.length > 0 && (
                              <p
                                className="mt-1 flex items-start gap-1 text-[12px] text-[var(--color-warning-fg)]"
                                title="A skill applies to every listed role in every listed department"
                              >
                                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                Also applies to:{" "}
                                {it.alsoApplies.map((p) => `${deptName(p.departmentId)} · ${roleName(p.role)}`).join(", ")}
                              </p>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {result.skippedRows.length > 0 && (
              <div className="rounded-md border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] px-4 py-3 text-[var(--color-warning-fg)]">
                <p className="font-medium">Skipped rows (not imported)</p>
                <ul className="mt-1 list-disc pl-5">
                  {result.skippedRows.map((r) => (
                    <li key={`${r.rowNumber}-${r.reason}`}>
                      Row {r.rowNumber}: {r.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.ignoredColumns.length > 0 && (
              <p className="text-[12px] text-surface-400">Ignored columns: {result.ignoredColumns.join(", ")}</p>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <SpecularButton size="sm" onClick={reset}>
                ← Choose another file
              </SpecularButton>
              <AsyncButton disabled={toImport.length === 0} onClick={() => commit()}>
                Import {toImport.length} skill{toImport.length === 1 ? "" : "s"}
              </AsyncButton>
            </>
          ) : (
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
